// PRD-1336 R4: decide whether a widget's cached result is stale under its refresh
// policy, so the Insights page serves the last computed result instead of
// re-querying on every load. Pure function — no clock side effects beyond `now`.
//
// Policies:
//   manual  → never auto-refreshes (only the manual button re-runs it)
//   hourly  → stale once an hour has passed since last_computed_at
//   daily   → stale once today's scheduled clock time (refresh_time) has passed
//             and the cached result predates it (anchored to a clock time, not to
//             the last run, so it doesn't drift — R3)
//   custom  → stale once refresh_interval_minutes has passed
//
// A widget with no last_computed_at (never computed) is always stale.

export function isWidgetStale(widget, now = new Date()) {
  if (!widget) return false;
  const policy = widget.refresh_policy || 'daily';
  if (policy === 'manual') return false;

  const last = widget.last_computed_at ? new Date(widget.last_computed_at) : null;
  if (!last || Number.isNaN(last.getTime())) return true;

  const elapsedMs = now.getTime() - last.getTime();

  if (policy === 'hourly') return elapsedMs >= 3600 * 1000;

  if (policy === 'custom') {
    const minutes = Number(widget.refresh_interval_minutes) || 60;
    return elapsedMs >= minutes * 60 * 1000;
  }

  if (policy === 'daily') {
    const [h, m] = String(widget.refresh_time || '08:00').split(':').map((n) => parseInt(n, 10));
    const scheduledToday = new Date(now);
    scheduledToday.setHours(Number.isFinite(h) ? h : 8, Number.isFinite(m) ? m : 0, 0, 0);
    // The most recent scheduled refresh: today's time if it has passed, else yesterday's.
    const lastScheduled =
      scheduledToday.getTime() <= now.getTime()
        ? scheduledToday
        : new Date(scheduledToday.getTime() - 24 * 3600 * 1000);
    return last.getTime() < lastScheduled.getTime();
  }

  // Unknown policy → be safe and refresh.
  return true;
}
